import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Components
import { Button, Modal, ModalProps } from '@gif/shared-ui';

// Hooks
import { useAdobeTracking } from '../../../hooks';
import { translate } from '../../../configs';

const StyledMlbPlayModal = styled(Modal)`
  background-color: ${(props) => props.theme.surface};
  color: ${(props) => props.theme.fontColor};
  max-width: 30rem;
  padding: 0.5rem 1rem;
  text-align: center;
  width: fit-content;

  .closeModalIcon {
    background-color: ${(props) => props.theme.surface};
    border-color: ${(props) => props.theme.fontColor};
    color: ${(props) => props.theme.fontColor};
  }
`;

const TextCopyButton = styled(Button)<{ $active: boolean }>`
  background: ${(props) => (props.$active ? props.theme.copyTextBg : 'none')};
  border-bottom: solid 1.5px ${(props) => props.theme.fontColor};
  border-radius: 0;
  color: ${(props) => props.theme.fontColor};
  font-weight: 700;
  height: 1.5rem;
  margin: 0 auto;
  padding: 0;
  text-align: center;
  width: 100%;

  &:hover {
    background: ${(props) => (props.$active ? props.theme.copyTextBg : 'none')};
    color: ${(props) => props.theme.fontColor};
  }
`;

const Title = styled.h1`
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const BodyText = styled.p`
  font-weight: 300;
  margin-bottom: 0.5rem;
`;

const ClickableTextButton = styled.button<{ autofocus: string }>`
  font-size: 0.875rem;
  font-weight: 300;
  margin: 1rem auto;
  width: max-content;
  background: none;
  border: none;
  padding: 0;
  display: inline-block;
  &:hover {
    cursor: pointer;
  }
`;

const clickToCopyLinkText = translate('clickToCopyLink');

export interface BranchLinkModalProps extends ModalProps {
  bodyText: string;
  link: string;
  title: string;
}

export function BranchLinkModal({
  bodyText,
  link,
  title,
  ...rest
}: BranchLinkModalProps) {
  const [text, setText] = useState(clickToCopyLinkText);
  const [isActive, setIsActive] = useState(false);
  const clickableButtonRef = useRef<HTMLButtonElement>(null);

  const handleCopyText = useCallback((t: string) => {
    navigator.clipboard.writeText(t);
    setIsActive(true);
    setText('Link copied!');
    const timer = setTimeout(() => {
      setText(clickToCopyLinkText);
      setIsActive(false);
      clearTimeout(timer);
    }, 2000);
  }, []);

  // moves focus on render from close button to copy button
  useEffect(() => {
    if (clickableButtonRef?.current) {
      clickableButtonRef.current.focus();
    }
  }, []);

  // Analytics Page Tracking
  const { trackPageView } = useAdobeTracking();
  useEffect(() => {
    if (rest.isOpen) {
      trackPageView({
        type: 'Gaming Hub',
        subType: title,
      });
    }
  }, [rest.isOpen, title, trackPageView]);
  // End Analytics Page Tracking

  return (
    <StyledMlbPlayModal id="branchLinkModal" showCloseIcon {...rest}>
      <Title>{title}</Title>
      <BodyText>{bodyText}</BodyText>
      <TextCopyButton
        $active={isActive}
        data-testid="txtCopyBtn"
        ref={clickableButtonRef}
        aria-label={clickToCopyLinkText}
        type="button"
        onClick={() => handleCopyText(link)}
      >
        {link}
      </TextCopyButton>
      <ClickableTextButton
        autofocus=""
        data-testid="copyBtn"
        onClick={() => handleCopyText(link)}
      >
        {text}
      </ClickableTextButton>
    </StyledMlbPlayModal>
  );
}

export default BranchLinkModal;
